import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';

import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useSyncedRequestContext } from '@providers/AppointmentProvider/SyncedRequestContext';
import { useAppointmentId } from '@store/appointment/selectors';

import { RequestID, requestLoadingMutation } from './useSyncedRequest';

/**
 * Syncs loading state for an action that involves multiple mutations.
 * Unlike useSyncedMutation (which syncs individual mutation loading states),
 * this hook wraps an entire action (e.g. Promise.all of multiple mutations)
 * and sends a single loading event to the remote client.
 *
 * Use this to avoid blocking the event stream with rapid sequential loading updates.
 */
const useSyncedAction = (id: RequestID) => {
  const appointmentId = useAppointmentId();
  const [requestLoading] = useMutation(requestLoadingMutation);
  const { hasControl } = useAppointmentContext();
  const { syncedRequestStates } = useSyncedRequestContext();

  const syncLoading = useCallback(
    (isLoading: boolean) => {
      if (!appointmentId || !hasControl || !id) {
        return;
      }

      requestLoading({
        variables: {
          id: appointmentId,
          request: id,
          isLoading,
        },
      });
    },
    [appointmentId, hasControl, id, requestLoading]
  );

  const updateActionLoading = useCallback(
    async <T>(promise: Promise<T>): Promise<T> => {
      syncLoading(true);
      try {
        return await promise;
      } finally {
        syncLoading(false);
      }
    },
    [syncLoading]
  );

  const syncedLoading = syncedRequestStates[id]?.isLoading ?? false;

  return { updateActionLoading, syncedLoading };
};

export { useSyncedAction };
